/**
 * Split view (Sep 2026) — on wide screens (≥1280px) the user can open a second
 * app beside the current one, e.g. USL next to Commissions. Opt-in only: the
 * top-bar button picks the app, the divider drags, × closes. Remembered per
 * browser (localStorage `daaily.split`).
 *
 * The second pane is a same-origin <iframe> of the hub itself, so each side
 * navigates on its own (one React Router can only hold one location). Inside
 * the frame Layout renders the page without its chrome (isEmbedded()). Auth is
 * shared (same origin, same IndexedDB session); Firestore rules and tool
 * guards apply in the pane exactly as in the main window.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Columns2, X } from 'lucide-react';
import { Icon } from '../design/Icon';
import { useSharedAppNav } from './useAppNav';
import { markUsed } from '../lib/usage';

let splitUser = '';
/** Per signed-in user (set by Layout), like the other per-user browser memory. */
export function setSplitUser(email: string) { splitUser = email.toLowerCase(); }
const key = () => `daaily.split:${splitUser}`;
const MIN_WIDE = 1280;

/** True when this window is the hub rendered inside the split pane. */
export function isEmbedded(): boolean {
  try { return window.self !== window.top && window.parent.location.origin === window.location.origin; } catch { return false; }
}

/**
 * Inside the pane: forward activity + keyboard shortcuts to the main window, so
 * the app-lock idle timer sees work done in the pane and ⌘K / Esc still reach
 * the main window's palette and drawers. Returns a cleanup.
 */
export function forwardEmbeddedEvents(): () => void {
  if (!isEmbedded()) return () => undefined;
  const parent = window.parent;
  const onPointer = (e: Event) => parent.dispatchEvent(new Event(e.type));
  const onKey = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') e.preventDefault();
    // Plain keys typed into a field stay in the pane: the main window's `?` and
    // `g` shortcuts can't tell a forwarded keystroke came from an input.
    const t = e.target as HTMLElement | null;
    const typing = !!t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName));
    if (typing && !e.metaKey && !e.ctrlKey && e.key !== 'Escape') return;
    parent.dispatchEvent(new KeyboardEvent('keydown', { key: e.key, metaKey: e.metaKey, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, altKey: e.altKey }));
  };
  window.addEventListener('pointerdown', onPointer, { passive: true });
  window.addEventListener('touchstart', onPointer, { passive: true });
  window.addEventListener('keydown', onKey);
  return () => {
    window.removeEventListener('pointerdown', onPointer);
    window.removeEventListener('touchstart', onPointer);
    window.removeEventListener('keydown', onKey);
  };
}

interface SplitState { route: string; width: number }

function load(): SplitState | null {
  // Only same-app paths ("/usl"), never a full or protocol-relative URL.
  try {
    const v = JSON.parse(localStorage.getItem(key()) || 'null');
    return v && typeof v.route === 'string' && v.route.startsWith('/') && !v.route.startsWith('//') ? v : null;
  } catch { return null; }
}
function save(v: SplitState | null) {
  if (v) markUsed('split');
  try { v ? localStorage.setItem(key(), JSON.stringify(v)) : localStorage.removeItem(key()); } catch { /* ignore */ }
  window.dispatchEvent(new Event('daaily-split'));
}

function useWide() {
  const [wide, setWide] = useState(() => window.innerWidth >= MIN_WIDE);
  useEffect(() => {
    const on = () => setWide(window.innerWidth >= MIN_WIDE);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return wide;
}

function useSplit(): SplitState | null {
  const [s, setS] = useState(load);
  useEffect(() => {
    const on = () => setS(load());
    window.addEventListener('daaily-split', on);
    return () => window.removeEventListener('daaily-split', on);
  }, []);
  return s;
}

/** Top-bar control: pick an app for the side pane (wide screens only). */
export function SplitButton() {
  const { navGroups } = useSharedAppNav();
  const split = useSplit();
  const wide = useWide();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  if (!wide || isEmbedded()) return null;
  const apps = [{ to: '/', label: 'Home' }, ...navGroups.flatMap((g) => g.items), { to: '/guide', label: 'Guide' }];
  return (
    <div className="relative flex" ref={ref}>
      <button
        type="button"
        onClick={() => (split ? save(null) : setOpen((o) => !o))}
        title={split ? 'Close split view' : 'Open a second app side by side'}
        aria-label={split ? 'Close split view' : 'Split view'}
        aria-pressed={!!split}
        className={`flex items-center transition-colors duration-fast ${split ? 'text-brand' : 'text-fg-muted hover:text-fg'}`}
      >
        <Icon as={Columns2} size={20} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 max-h-[70vh] overflow-y-auto rounded-lg border border-line bg-surface shadow-lg z-dropdown p-1">
          <p className="px-3 pt-2 pb-1 text-xs text-fg-muted">Open beside this page:</p>
          {apps.map((a) => (
            <button
              key={a.to}
              type="button"
              onClick={() => { save({ route: a.to, width: load()?.width ?? 50 }); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm text-fg-muted hover:text-fg hover:bg-surface-2 rounded-md"
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Wraps the page: renders the side pane next to it when a split is open. */
export function SplitFrame({ children, reloadKey }: { children: ReactNode; /** Changing it reloads the pane. */ reloadKey?: string }) {
  const split = useSplit();
  const wide = useWide();
  // Fill from the box's top edge to the viewport bottom, whatever banners sit above it.
  const [height, setHeight] = useState<number | null>(null);
  useEffect(() => {
    if (!split || !wide) return;
    const measure = () => { const t = box.current?.getBoundingClientRect().top ?? 0; setHeight(Math.max(320, window.innerHeight - t)); };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    ro.observe(document.body);
    return () => { window.removeEventListener('resize', measure); ro.disconnect(); };
  }, [split, wide]);
  const [width, setWidth] = useState(split?.width ?? 50);
  const widthRef = useRef(width);
  widthRef.current = width;
  const [dragging, setDragging] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => { if (split) setWidth(split.width); }, [split]);

  useEffect(() => {
    if (!dragging) return;
    const move = (e: PointerEvent) => {
      const r = box.current?.getBoundingClientRect();
      if (!r) return;
      setWidth(Math.min(70, Math.max(30, ((r.right - e.clientX) / r.width) * 100)));
    };
    const up = () => { setDragging(false); const s = load(); if (s) save({ ...s, width: Math.round(widthRef.current) }); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  }, [dragging]);

  if (!split || !wide || isEmbedded()) return <>{children}</>;
  return (
    <div ref={box} className="flex min-h-0 flex-1" style={{ height: height ?? 'calc(100vh - 3.5rem)' }}>
      <div className="min-w-0 overflow-y-auto" style={{ flex: `1 1 ${100 - width}%` }}>{children}</div>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize split view"
        onPointerDown={(e) => { e.preventDefault(); setDragging(true); }}
        className={`w-1.5 shrink-0 cursor-col-resize transition-colors duration-fast ${dragging ? 'bg-brand-solid' : 'bg-line hover:bg-line-strong'}`}
      />
      <div className="relative min-w-0 flex flex-col border-l border-line" style={{ flex: `1 1 ${width}%` }}>
        <button
          type="button"
          onClick={() => save(null)}
          aria-label="Close split view"
          className="absolute top-2 right-2 z-sticky inline-flex h-7 w-7 items-center justify-center rounded-md bg-surface border border-line text-fg-faint hover:text-fg"
        >
          <Icon as={X} size={14} />
        </button>
        {/* pointer-events off while dragging, or the iframe swallows the pointer. */}
        <iframe
          key={`${split.route}|${reloadKey ?? ''}`}
          src={split.route}
          title="Split view"
          data-split-pane
          className="flex-1 w-full bg-app"
          style={{ pointerEvents: dragging ? 'none' : undefined }}
        />
      </div>
    </div>
  );
}
